import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AudioButton, ChoiceButton, ExerciseCard, Muted, Stack } from '@fluent/ui';
import type { Cloze as ClozeExercise } from '@fluent/types';
import { useAudio } from '../audio/audioPlayer.js';
import './Cloze.css';

export interface ClozeProps {
  exercise: ClozeExercise;
  /** Display order for the options — defaults to authored order. */
  options?: readonly string[];
  selected: string | null;
  onSelectedChange: (next: string) => void;
  locked: boolean;
  lastVerdict: 'exact' | 'wrong' | 'close' | null;
}

/**
 * Cloze — fill-in-the-blank. Sentence with a single `___` blank; user picks
 * the right word from 2–4 options. Tests grammar precisely (e.g. "I ___
 * Giorgi" with options am/are/is) in a way translate_typed can't.
 *
 * Render: split prompt_en on `___`, render the parts as plain text with the
 * blank shown as a styled placeholder that fills with the picked option
 * (or stays empty until selection). Below: option chips.
 *
 * Selection state lives in the parent (LessonPlayer) — same pattern as
 * other tap-driven exercises.
 */
export function Cloze({
  exercise,
  options,
  selected,
  onSelectedChange,
  locked,
  lastVerdict,
}: ClozeProps) {
  const { t } = useTranslation();
  const audio = useAudio(exercise.audio_key);
  const autoPlayedRef = useRef<string | null>(null);

  // The clip reads the completed sentence — i.e. the answer. Sealed until
  // the user commits, then played once as reinforcement.
  useEffect(() => {
    if (lastVerdict == null || autoPlayedRef.current === exercise.id) return;
    autoPlayedRef.current = exercise.id;
    audio.play();
  }, [lastVerdict, exercise.id, audio]);

  // Split on the FIRST occurrence of `___` (validator enforces exactly one).
  const blankIdx = exercise.prompt_en.indexOf('___');
  const before = exercise.prompt_en.slice(0, blankIdx);
  const after = exercise.prompt_en.slice(blankIdx + 3);

  const filledWord = selected ?? '';

  return (
    <ExerciseCard kicker={t('lesson.cloze.kicker')}>
      <Muted className="fluent-cloze__context" lang="ka">
        {exercise.prompt_ka}
      </Muted>
      <p className="fluent-cloze__sentence" lang="en">
        <span>{before}</span>
        <span
          className="fluent-cloze__blank"
          data-filled={Boolean(filledWord) || undefined}
          data-state={lastVerdict ?? 'idle'}
        >
          {filledWord || '        '}
        </span>
        <span>{after}</span>
      </p>
      {exercise.audio_key && lastVerdict != null && (
        <div className="fluent-cloze__audio-row">
          <AudioButton
            size="sm"
            playing={audio.playing}
            onClick={() => (audio.playing ? audio.stop() : audio.play())}
            label={t('lesson.cloze.audio_aria')}
          />
          
        </div>
      )}
      <Stack gap={2} className="fluent-cloze__options">
        {(options ?? exercise.options).map((opt, idx) => {
          const isSelected = selected === opt;
          const isAnswer = opt === exercise.answer;
          const state =
            lastVerdict == null
              ? 'idle'
              : isAnswer
                ? 'correct'
                : isSelected
                  ? 'wrong'
                  : 'idle';
          return (
            <ChoiceButton
              key={opt}
              lang="en"
              bullet={String.fromCharCode(65 + idx)}
              selected={isSelected}
              state={state}
              disabled={locked}
              onClick={() => onSelectedChange(opt)}
            >
              {opt}
            </ChoiceButton>
          );
        })}
      </Stack>
    </ExerciseCard>
  );
}
